// FILE: src/services/authIdentity.js
//
// Single source of truth for "who owns this write" in fire-and-forget service
// code that runs OUTSIDE the React tree (sessionService, questionAttemptsService,
// telemetryService, …). Those paths cannot read LearnerAuthContext, so before
// this module they each called `supabase.auth.getUser()` at save time.
//
// Why that was wrong (the bug this module fixes):
//   • getUser() makes a NETWORK round-trip to validate the JWT. On any transient
//     failure it returns null, and the caller wrote `user_id = NULL` — silently
//     converting a signed-in learner's exam into an unattributed "guest" row.
//   • That produced registered accounts with zero linked activity and a large
//     pool of orphaned source='user' rows.
//
// The ownership model this module establishes:
//   • Identity comes from the ESTABLISHED auth state — the in-memory session
//     mirrored from supabase.auth.onAuthStateChange, seeded once from the LOCAL
//     getSession() read (localStorage, no network). Same signal the React
//     LearnerAuthContext trusts.
//   • resolveWriteOwner() NEVER silently downgrades an authenticated learner to
//     guest on lookup failure. If auth state is genuinely unknown it throws an
//     AttributionError so the caller can record it (telemetry) and let the
//     database trigger (auth.uid()) attribute the row — instead of writing NULL.
//   • A truly signed-out learner still resolves to { isGuest: true, userId: null }.
//     Intentional guest usage is preserved.
//
// Defense in depth: migration 057 adds a BEFORE INSERT trigger that forces
// user_id = auth.uid() for any authenticated insert, so the database — not the
// client — is the final authority on ownership. This module keeps the client's
// own post-save logic (readiness/achievements) correctly gated and feeds
// guest/authenticated/failure telemetry.

import { supabase } from './supabaseClient';

/** Thrown when auth state cannot be established. Callers must NOT treat this as
 *  "guest" — an authenticated learner would be mis-attributed to NULL. */
export class AttributionError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'AttributionError';
  }
}

// In-memory mirror of the current auth user. `null` once we have confirmed a
// signed-out state; `undefined` while we have never observed auth state.
let cachedUserId;          // string | null | undefined
let subscribed = false;

/** Idempotently subscribe to auth-state changes. Safe to call many times. */
export function initAuthIdentity() {
  if (subscribed) return;
  subscribed = true;
  try {
    // Seed from the LOCAL session (no network). Best-effort.
    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (error) return; // leave cache undefined → resolveWriteOwner will retry
        cachedUserId = data?.session?.user?.id || null;
      })
      .catch(() => { /* leave undefined; resolve() will read getSession again */ });

    supabase.auth.onAuthStateChange((_event, session) => {
      cachedUserId = session?.user?.id || null;
    });
  } catch (_) {
    // Auth wiring optional (e.g. some unit-test mocks). resolveWriteOwner()
    // falls back to a direct getSession() read.
  }
}

/** Synchronous best-effort read of the cached owner id. May be undefined before
 *  the first auth-state observation. Prefer resolveWriteOwner() for writes. */
export function getCachedUserId() {
  return cachedUserId ?? null;
}

/**
 * Resolve the owner for a write.
 *
 * @returns {Promise<{ userId: string|null, isGuest: boolean }>}
 *   - Authenticated: { userId: <uuid>, isGuest: false }
 *   - Signed out:    { userId: null,   isGuest: true }
 * @throws {AttributionError} when auth state cannot be established. Callers must
 *   NOT fall back to guest — emit telemetry and let the DB trigger attribute.
 */
export async function resolveWriteOwner() {
  initAuthIdentity();

  // 1. Fast path: a confirmed cached identity (authenticated OR signed-out).
  if (cachedUserId !== undefined) {
    return cachedUserId
      ? { userId: cachedUserId, isGuest: false }
      : { userId: null, isGuest: true };
  }

  // 2. Cache not warmed yet — read the LOCAL session directly (no network).
  let data;
  let error;
  try {
    ({ data, error } = await supabase.auth.getSession());
  } catch (err) {
    // Lookup itself threw — auth state is unknown. Refuse to guess "guest".
    throw new AttributionError('auth session lookup failed', { cause: err });
  }
  if (error) {
    throw new AttributionError(`auth session lookup error: ${error.message}`);
  }

  const userId = data?.session?.user?.id || null;
  cachedUserId = userId; // warm the cache
  return userId ? { userId, isGuest: false } : { userId: null, isGuest: true };
}

/** Test-only: reset module state between test cases. */
export function __resetAuthIdentityForTests() {
  cachedUserId = undefined;
  subscribed = false;
}
